import React, { useState, useMemo, useCallback } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, addMonths, subMonths,
         startOfMonth, endOfMonth, isToday, isSameMonth, isSameDay } from 'date-fns';
import useWindowSize from '../hooks/useWindowSize';
import MealPlanifyModalPreview from './MealPlanifyModalPreview';
import MonthDayMeals from './MonthDayMeals';
import './MealPlan.css';
import { toast } from 'react-hot-toast';

const getMealTypeStyle = (mealType) => {
  switch (mealType?.toLowerCase()) {
    case 'breakfast': return { icon: 'fa-sun', color: '#f59e0b' };
    case 'lunch': return { icon: 'fa-cloud-sun', color: '#3b82f6' };
    case 'dinner': return { icon: 'fa-moon', color: '#8b5cf6' };
    case 'snack': return { icon: 'fa-cookie-bite', color: '#10b981' };
    default: return { icon: 'fa-utensils', color: '#8b5cf6' };
  }
};

const today = new Date();
const mockMeals = [
  {
    id: 'mock-1',
    title: 'Spiced Lamb Keema: A Flavorful Minced Meat Dish',
    date: addDays(today, 1),
    mealType: 'Dinner',
    image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400'
  },
  {
    id: 'mock-2',
    title: 'Cozy Chicken Arroz Caldo: Filipino Rice Porridge',
    date: addDays(today, 2),
    mealType: 'Dinner',
    image: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=400'
  },
  {
    id: 'mock-3',
    title: 'Tropical Chicken Curry with Sweet Pineapple',
    date: addDays(today, 3),
    mealType: 'Dinner',
    image: 'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?w=400'
  }
];

const MealPlanPreview = () => {
  const [isMealPlanifyOpen, setIsMealPlanifyOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('week');
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [monthDate, setMonthDate] = useState(new Date());
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const handleFeatureClick = useCallback((featureName) => {
    toast.error(`Please sign in to use ${featureName}`, { duration: 3000, icon: '🔒' });
  }, []);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const weekEnd = addDays(weekStart, 6);

  const getMealsForDay = useCallback((day) =>
    mockMeals.filter(m => isSameDay(m.date, day)),
    []
  );

  const getMonthGrid = useCallback(() => {
    const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
    const end = endOfMonth(monthDate);
    const weeks = [];
    let day = start;
    while (day <= end || weeks.length < 5) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(day);
        day = addDays(day, 1);
      }
      weeks.push(week);
      if (weeks.length >= 6) break;
    }
    return weeks;
  }, [monthDate]);

  const renderMockMealCard = (meal) => {
    const style = getMealTypeStyle(meal.mealType);
    return (
      <div
        key={meal.id}
        className="mp-meal-card"
        style={{ borderLeftColor: style.color }}
        onClick={() => handleFeatureClick('meal details')}
      >
        <div className="mp-meal-header">
          <i className={`fas ${style.icon}`} style={{ color: style.color }}></i>
          <span className="mp-meal-type">{meal.mealType}</span>
        </div>
        <div className="mp-meal-body">
          {meal.image && (
            <img src={meal.image} alt={meal.title} className="mp-meal-image" />
          )}
          <div className="mp-meal-info">
            <h4 className="mp-meal-name">{meal.title}</h4>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mp-page">
      <div className="meal-plan-decision-banner preview-mode">
        <div
          className="meal-plan-decision-option link"
          onClick={() => setIsMealPlanifyOpen(true)}
          role="button"
          tabIndex={0}
        >
          <span className="decision-icon">🟢</span>
          <div>
            <strong>Simple Meal Planning</strong>
            <p>Generate meals for the week based on your preferences</p>
          </div>
          <span className="decision-arrow">→</span>
        </div>
        <div className="meal-plan-decision-divider">or</div>
        <div
          className="meal-plan-decision-option link"
          onClick={() => handleFeatureClick('nutrition planner')}
          role="button"
          tabIndex={0}
        >
          <span className="decision-icon">🔵</span>
          <div>
            <strong>Nutrition Planner</strong>
            <p>Control calories, macros &amp; protein targets</p>
          </div>
          <span className="decision-arrow">→</span>
        </div>
      </div>

      <div className="mp-actions">
        <button
          className={`mp-action-btn planify${isMealPlanifyOpen ? ' active' : ''}`}
          onClick={() => setIsMealPlanifyOpen(prev => !prev)}
        >
          <i className="fas fa-magic"></i> MealPlanify
        </button>
        <button
          className="mp-action-btn select disabled-preview"
          onClick={() => handleFeatureClick('date selection')}
        >
          <i className="fas fa-calendar-check"></i>
          Select Dates
        </button>
        <div className="mp-preview-lock-banner">
          <i className="fas fa-lock"></i>
          This is a preview. Sign in to add meals, generate meal plans, and shop for ingredients.
        </div>
      </div>

      {isMealPlanifyOpen && (
        <MealPlanifyModalPreview
          isOpen={isMealPlanifyOpen}
          onClose={() => setIsMealPlanifyOpen(false)}
        />
      )}

      <div className="mp-calendar-nav">
        <button
          className="mp-nav-btn"
          onClick={() => calendarView === 'week'
            ? setWeekStart(subWeeks(weekStart, 1))
            : setMonthDate(subMonths(monthDate, 1))}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <div className="mp-nav-label">
          {calendarView === 'week'
            ? <span className="mp-nav-dates">{format(weekStart, 'MMM d')} — {format(weekEnd, 'MMM d, yyyy')}</span>
            : <span className="mp-nav-dates">{format(monthDate, 'MMMM yyyy')}</span>}
        </div>
        <button
          className="mp-nav-btn"
          onClick={() => calendarView === 'week'
            ? setWeekStart(addWeeks(weekStart, 1))
            : setMonthDate(addMonths(monthDate, 1))}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
        <button
          className="mp-today-btn"
          onClick={() => {
            setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
            setMonthDate(new Date());
          }}
        >
          Today
        </button>
        <div className="mp-view-toggle">
          <button
            className={`mp-view-btn ${calendarView === 'week' ? 'active' : ''}`}
            onClick={() => setCalendarView('week')}
          >
            Week
          </button>
          <button
            className={`mp-view-btn ${calendarView === 'month' ? 'active' : ''}`}
            onClick={() => setCalendarView('month')}
          >
            Month
          </button>
        </div>
      </div>

      {calendarView === 'week' && (
        <div className="mp-week-grid">
          {weekDays.map(day => {
            const dayMeals = getMealsForDay(day);
            const todayFlag = isToday(day);
            return (
              <div key={day.toISOString()} className={`mp-day-col ${todayFlag ? 'today' : ''}`}>
                <div className="mp-day-header">
                  <span className="mp-day-name">{format(day, 'EEE')}</span>
                  <span className={`mp-day-date ${todayFlag ? 'today-accent' : ''}`}>{format(day, 'd')}</span>
                </div>
                <div className="mp-day-items">
                  {dayMeals.length === 0 ? (
                    <div className="mp-day-empty">No meals</div>
                  ) : (
                    dayMeals.map(meal => renderMockMealCard(meal))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {calendarView === 'month' && (
        <div className="mp-month-grid">
          <div className="mp-month-header-row">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="mp-month-header-cell">{d}</div>
            ))}
          </div>
          {getMonthGrid().map((week, wi) => (
            <div key={wi} className="mp-month-week-row">
              {week.map(day => {
                const dayMeals = getMealsForDay(day);
                const todayFlag = isToday(day);
                const inMonth = isSameMonth(day, monthDate);
                return (
                  <div
                    key={day.toISOString()}
                    className={`mp-month-cell ${todayFlag ? 'today' : ''} ${!inMonth ? 'outside' : ''} ${dayMeals.length ? 'has-meals' : ''}`}
                  >
                    <span className={`mp-month-day-num ${todayFlag ? 'today-accent' : ''}`}>{format(day, 'd')}</span>
                    <MonthDayMeals
                      meals={dayMeals}
                      maxPills={isMobile ? 2 : 3}
                      getMealName={(meal) => meal.title || 'Item'}
                      onMealClick={() => handleFeatureClick('meal details')}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MealPlanPreview;
